/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import ApplicationFeatureFlags from '@ion/core/src/security/ApplicationFeatureFlags';

/**
 * Provides the Authoritative list of Feature Flags which are supported by ADM.
 * The `FeatureFlag` enumerations defined herein represent ADM aware Role based
 * Features, and / or UI Only Features.
 *
 *   - APM: When enabled, indicates that the APM Framework is to be enabled:
 *     "?features=apm"
 *   - APM_DEBUG: When enabled, indicates that the APM Framework is to be
 *     enabled, but it is only to log results and bypass emitting results
 *     to the underlying endpoint: "?features=apmdebug"
 *   - LIVE_JOBS: When enabled, indicates that WebSocket driven live job updates
 *     (the jobs grid, the open job tabs and the watched job notifications) are
 *     to be enabled: "?features=livejobs"
 *
 * @see @ion/applications/src/telemetry/APMInitializer#APMFeatureFlag
 */
export enum FeatureFlag {
  APM = 'apm',
  APM_DEBUG = 'apmdebug',
  VBU_FW = 'vbufw',
  BRAKE_ECU = 'brake',
  PEG_WIZARD = 'pegwizard',
  LIVE_JOBS = 'livejobs',
  REPORTS = 'reports'
};

/**
 * Provides mappings of pre-defined feature flag constants as keys where each key
 * represents a supported feature flag (which, for example, can be used in the
 * query string, or via a build argument, etc.).
 *
 * While a Feature Flag's keys and values can be arbitrarily defined, generally,
 * their values are either:
 *
 *   - A Role Feature Id (guid from ./Features): (either seeded and / or unseeded)
 *   - A boolean value of true (for non-Role based Feature Flags). Note the value
 *     of true simply indicates that the given feature flag is supported.
 *
 * The following examples demonstrates two pre-defined feature flags, the first
 * `SOME_PAGE` mapping to a Role Feature's GUID, and the second `SOME_COMPONENT`
 * having a value of `true`, which simply indicates that the feature flag is
 * supported (but is unrelated to Role Features):
 *
 * ```typescript
 *     import FeatureFlags from '@/security/FeatureFlags';
 *
 *     FeatureFlags.applyFeatureFlags(queryFeatures);
 *     const featureRoles = FeatureFlags.getRoleFeatures();
 *     Features.updateRoleFeatures(FeatureFlags.getRoleFeatures());
 *   ```
 */
class FeatureFlags extends ApplicationFeatureFlags {
  /**
   * Features are defined in key/value pairs. Key defines the query param value and value
   * corresponds to the feature in the application.
   *
   *  ```typescript
   *    import {FEATURE_PAGE} from './Features';
   *
   *    _feature = {
   *      FEATURE1 : FEATURE_PAGE
   *    }
   * ```
   */
  protected _features = {
    [FeatureFlag.APM]        : true,
    [FeatureFlag.APM_DEBUG]  : true,
    [FeatureFlag.VBU_FW]     : true,
    [FeatureFlag.BRAKE_ECU]  : true,
    [FeatureFlag.PEG_WIZARD] : true,
    [FeatureFlag.LIVE_JOBS]  : true,
    [FeatureFlag.REPORTS]    : true
  };

  /**
   * Provides an ADM specific overload of the base `isEnabled` method which allows
   * for determining if a given ADM specific `FeatureFlag` is `enabled`.
   *
   * ```typescript
   *   FeatureFlags.isEnabled(FeatureFlag.FM_TRACKING); // true if enabled, otherwise false
   * ```
   *
   * @param  feature A `FeatureFlag` for which to determine if it is `enabled`.
   * @return `true` if the given `FeatureFlag` is enabled, otherwise, `false`.
   */
  public isEnabled(feature: FeatureFlag): boolean {
    return super.isEnabled(feature);
  };

  /**
   * Provides an ADM specific overload of the base `isDisabled` method which allows
   * for determining if a given ADM specific `FeatureFlag` is `disabled`.
   *
   * ```typescript
   *   FeatureFlags.isDisabled(FeatureFlag.FM_TRACKING); // true if disabled, otherwise false
   * ```
   *
   * @param  feature A `FeatureFlag` for which to determine if it is `disabled`.
   * @return `true` if the given `FeatureFlag` is `disabled`, otherwise, `false`.
   */
  public isDisabled(feature: FeatureFlag): boolean {
    return super.isDisabled(feature);
  }
};

export default new FeatureFlags();
